<div class="billing-settings settings-page">

  <div class="settings-page__content">
    <p-tabs [value]="activeTab()" (valueChange)="onTabChange('' + $event)">
      <p-tablist>
        <p-tab value="plan">{{ 'SETTINGS_BILLING.TABS.PLAN' | translate }}</p-tab>
        <p-tab value="profile">{{ 'SETTINGS_BILLING.TABS.PROFILE' | translate }}</p-tab>
        <p-tab value="invoices">{{ 'SETTINGS_BILLING.TABS.INVOICES' | translate }}</p-tab>
        @if (canShowCancellationTab()) {
          <p-tab value="cancellation">{{ 'SETTINGS_BILLING.TABS.CANCELLATION' | translate }}</p-tab>
        }
      </p-tablist>

      <p-tabpanels>
        <p-tabpanel value="plan">
          <div class="billing-tab-content">
            @if (showInitialOverviewError()) {
              <tf-settings-load-error
                [title]="'SETTINGS_BILLING.ERROR.LOAD_FAILED_TITLE' | translate"
                [message]="'SETTINGS_BILLING.ERROR.LOAD_FAILED_DESCRIPTION' | translate"
                [details]="overviewLoadErrorDetails()"
                [dense]="true"
                [retrying]="isLoadingOverview()"
                (retry)="refresh()" />
            } @else if (showInitialOverviewLoading()) {
              <section class="billing-section billing-plan-skeleton" aria-hidden="true">
                <div class="billing-skeleton-card billing-skeleton-card--plan">
                  <div class="billing-plan-skeleton__header">
                    <div>
                      <span class="billing-skeleton-bar billing-skeleton-bar--title"></span>
                      <span class="billing-skeleton-bar billing-skeleton-bar--description"></span>
                    </div>
                    <span class="billing-skeleton-bar billing-skeleton-bar--price"></span>
                  </div>
                  <div class="billing-plan-skeleton__meta">
                    <span class="billing-skeleton-bar"></span>
                    <span class="billing-skeleton-bar"></span>
                  </div>
                  <span class="billing-skeleton-bar billing-skeleton-bar--wide"></span>
                  <span class="billing-skeleton-button billing-skeleton-button--right"></span>
                </div>

                <div class="billing-skeleton-card billing-skeleton-card--usage">
                  <span class="billing-skeleton-bar billing-skeleton-bar--title"></span>
                  <div class="billing-plan-skeleton__meters">
                    @for (_ of billingSkeletonRows; track _) {
                      <div class="billing-plan-skeleton__meter">
                        <span class="billing-skeleton-bar billing-skeleton-bar--medium"></span>
                        <span class="billing-skeleton-bar billing-skeleton-bar--wide"></span>
                      </div>
                    }
                  </div>
                </div>

                <div class="billing-compare-skeleton">
                  <span class="billing-skeleton-bar billing-skeleton-bar--title"></span>
                  <tf-settings-loading-placeholder [rows]="5" [columns]="4" />
                </div>
              </section>
            } @else if (currentPlan(); as plan) {
              @let kind = plan.planKind;
              @let isFree = kind === planKinds.Free;
              @let isEnterprise = kind === planKinds.Enterprise;
              @let isPaidSelfService = kind === planKinds.SelfServe;
              @let resourceMeterRows = billingResourceMeterRows();
              @let aiMeters = billingAiUsageMeters();
              @let seats = seatUsage();
              @let seatsLimit = seats?.limit ?? 0;
              @let seatsUsed = seats?.used ?? 0;
              @let seatsRemaining = seatsRemainingCount();
              @let seatsUsedPct = seatsUsedPercent();
              @let seatsCapless = seatsUnlimited();
              @let allSeatsInUse = !seatsCapless && seatsLimit > 0 && seatsUsed >= seatsLimit;

              <section class="billing-section">
                <tf-settings-inline-message
                  [message]="
                    showOverviewReloadError()
                      ? ('SETTINGS_BILLING.ERROR.LOAD_FAILED_DESCRIPTION' | translate)
                      : null
                  "
                  messageId="billing-overview-reload-error"
                  (dismissed)="dismissOverviewReloadError()" />

                <article class="billing-header-card">
                  <header class="billing-header-card__head">
                    <div class="billing-header-card__intro">
                      <h2 class="billing-header-card__name">{{ plan.name }}</h2>
                      @if (plan.description) {
                        <p class="billing-header-card__sub">{{ plan.description }}</p>
                      }
                    </div>
                    @if (isPaidSelfService) {
                      <div class="billing-header-card__head-right">
                        <div class="billing-header-card__price">
                          @if (isTranslationKey(formatPlanPrimaryPrice(plan))) {
                            {{ formatPlanPrimaryPrice(plan) | translate }}
                          } @else {
                            {{ formatPlanPrimaryPrice(plan) }}
                          }
                        </div>
                        @if (planPriceSuffix(plan); as suffix) {
                          <div class="billing-header-card__price-meta">
                            @if (isTranslationKey(suffix)) {
                              {{ suffix | translate }}
                            } @else {
                              {{ suffix }}
                            }
                          </div>
                        }
                        <div class="billing-header-card__price-vat">
                          {{ 'SETTINGS_BILLING.PRICE_EXCLUDES_VAT' | translate }}
                        </div>
                      </div>
                    } @else if (isEnterprise) {
                      <div class="billing-header-card__head-right">
                        <p-tag
                          class="billing-header-card__tag tf-tag-sm"
                          [value]="'SETTINGS_BILLING.COMPARE.ENTERPRISE.NAME' | translate"
                          [rounded]="true" />
                      </div>
                    }
                  </header>

                  @if (isBillingStatusWarning()) {
                    <p-tag
                      class="billing-header-card__status tf-tag-sm"
                      [value]="billingStatusLabelKey() | translate"
                      severity="warn"
                      [rounded]="true"
                      aria-live="polite" />
                  }

                  @if (isPaidSelfService) {
                    <dl class="billing-header-card__meta">
                      @if (billingPeriodLabel(); as period) {
                        <div>
                          <dt>{{ 'SETTINGS_BILLING.PLAN.BILLING_PERIOD' | translate }}</dt>
                          <dd>{{ period }}</dd>
                        </div>
                      }
                      @if (nextInvoiceLabel(); as nextInvoice) {
                        <div>
                          <dt>{{ 'SETTINGS_BILLING.PLAN.NEXT_INVOICE' | translate }}</dt>
                          <dd>{{ nextInvoice }}</dd>
                        </div>
                      }
                    </dl>
                  } @else if (usagePeriodLabel(); as usagePeriod) {
                    <dl class="billing-header-card__meta">
                      <div>
                        <dt>{{ 'SETTINGS_BILLING.PLAN.USAGE_PERIOD' | translate }}</dt>
                        <dd>{{ usagePeriod }}</dd>
                      </div>
                    </dl>
                  }

                  <section class="billing-header-card__seats">
                    <div class="billing-header-card__seats-main">
                      <div class="billing-header-card__seats-head">
                        <div class="billing-header-card__seats-head-left">
                          <span class="billing-header-card__seats-label">
                            {{ 'SETTINGS_BILLING.PLAN.SEATS.LABEL' | translate }}
                          </span>
                          <span class="billing-header-card__seats-count">
                            <strong>{{ seatsUsed }}</strong>
                            <span class="billing-header-card__muted">
                              {{ 'SETTINGS_BILLING.PLAN.SEATS.ACTIVE_LABEL' | translate }}
                            </span>
                          </span>
                          @if (!seatsCapless) {
                            <span class="billing-header-card__seats-divider">·</span>
                            <span class="billing-header-card__seats-count">
                              <strong>{{ seatsLimit }}</strong>
                              <span class="billing-header-card__muted">
                                @if (isEnterprise) {
                                  {{ 'SETTINGS_BILLING.PLAN.SEATS.NEGOTIATED_LABEL' | translate }}
                                } @else if (isFree) {
                                  {{ 'SETTINGS_BILLING.PLAN.SEATS.INCLUDED_LABEL' | translate }}
                                } @else {
                                  {{ 'SETTINGS_BILLING.PLAN.SEATS.PURCHASED_LABEL' | translate }}
                                }
                              </span>
                            </span>
                          }
                        </div>
                        <div class="billing-header-card__seats-head-right">
                          @if (isPaidSelfService && seatsPricePerSeatMinor(); as price) {
                            <span class="billing-header-card__seats-rate">
                              {{
                                'SETTINGS_BILLING.PLAN.SEATS.RATE_PER_SEAT_MONTH'
                                  | translate: { price: formatAmount(price, seatsCurrency()) }
                              }}
                            </span>
                          }
                        </div>
                      </div>
                      @if (!seatsCapless) {
                        <div class="billing-header-card__seats-bar" aria-hidden="true">
                          <span
                            class="billing-header-card__seats-bar-segment"
                            [class.billing-header-card__seats-bar-segment--warning]="allSeatsInUse"
                            [style.width.%]="seatsUsedPct"></span>
                        </div>
                      }
                      <div class="billing-header-card__seats-foot">
                        @if (seatsCapless && isEnterprise) {
                          <span class="billing-header-card__muted">
                            {{ 'SETTINGS_BILLING.PLAN.SEATS.UNLIMITED_HINT' | translate }}
                          </span>
                        } @else if (!allSeatsInUse && seatsRemaining > 0) {
                          <span class="billing-header-card__muted">
                            {{
                              (seatsRemaining === 1
                                ? 'SETTINGS_BILLING.PLAN.SEATS.REMAINING_ONE'
                                : 'SETTINGS_BILLING.PLAN.SEATS.REMAINING_MANY'
                              ) | translate: { count: seatsRemaining }
                            }}
                          </span>
                        } @else if (allSeatsInUse && isFree) {
                          <span class="billing-header-card__muted">
                            {{ 'SETTINGS_BILLING.PLAN.SEATS.UPGRADE_HINT' | translate }}
                          </span>
                        } @else if (allSeatsInUse && isEnterprise) {
                          <span class="billing-header-card__warning">
                            <i class="pi pi-exclamation-circle" aria-hidden="true"></i>
                            {{ 'SETTINGS_BILLING.PLAN.SEATS.ALL_IN_USE_ENTERPRISE' | translate }}
                          </span>
                        } @else if (allSeatsInUse) {
                          <span class="billing-header-card__warning">
                            <i class="pi pi-exclamation-circle" aria-hidden="true"></i>
                            {{ 'SETTINGS_BILLING.PLAN.SEATS.ALL_IN_USE' | translate }}
                          </span>
                        }
                        @if (seatChangeStarted()) {
                          <span class="billing-header-card__success">
                            {{ 'SETTINGS_BILLING.PLAN.SEATS.CHANGE_STARTED' | translate }}
                          </span>
                        }
                        @if (pendingChange()?.status === 'SCHEDULED_PERIOD_END') {
                          <span class="billing-header-card__success">
                            {{
                              'SETTINGS_BILLING.PLAN.SEATS.SCHEDULED'
                                | translate
                                  : {
                                      seats: pendingChangeSeatQuantity(),
                                      date: formatDate(pendingChange()?.effectiveAt),
                                    }
                            }}
                          </span>
                        }
                      </div>
                    </div>
                    <div class="billing-header-card__seats-action">
                      @if (canChangeSeats()) {
                        <p-button
                          [label]="'SETTINGS_BILLING.PLAN.SEATS.UPDATE_BUTTON' | translate"
                          icon="pi pi-pencil"
                          [disabled]="isChangingSeats()"
                          (onClick)="openSeatDialog()" />
                      } @else if (isFree) {
                        <p-button
                          severity="secondary"
                          [outlined]="true"
                          [disabled]="true"
                          [label]="'SETTINGS_BILLING.PLAN.SEATS.UPGRADE_PLAN_BUTTON' | translate" />
                      }
                    </div>
                  </section>

                  <tf-settings-inline-message
                    [message]="seatChangeFailed() ? (seatChangeErrorMessageKey() | translate) : null"
                    messageId="billing-seat-change-error"
                    (dismissed)="dismissSeatChangeError()" />
                </article>

                <article class="billing-usage-card">
                  <header class="billing-usage-card__head">
                    <h3>{{ 'SETTINGS_BILLING.PLAN.USAGE_TITLE' | translate }}</h3>
                  </header>

                  @if (hasUsageMeterLoadFailures()) {
                    <div class="billing-usage-card__meter-alert" role="alert" aria-live="assertive">
                      <div class="billing-usage-card__meter-alert-copy">
                        <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
                        <p>
                          <strong>
                            {{ 'SETTINGS_BILLING.PLAN.METERS.LOAD_FAILED_TITLE' | translate }}
                          </strong>
                          <span>
                            {{ 'SETTINGS_BILLING.PLAN.METERS.LOAD_FAILED_DETAIL' | translate }}
                          </span>
                        </p>
                      </div>
                      <p-button
                        size="small"
                        severity="secondary"
                        [outlined]="true"
                        icon="pi pi-refresh"
                        [loading]="isLoadingUsageMeters()"
                        [disabled]="isLoadingUsageMeters()"
                        [label]="'SETTINGS_BILLING.PLAN.METERS.RETRY' | translate"
                        (onClick)="retryUsageMeters()" />
                    </div>
                  }

                  @for (row of resourceMeterRows; track (row[0]?.id ?? '') + '-' + (row[1]?.id ?? '')) {
                    <div class="billing-usage-card__group billing-usage-card__group--divided">
                      @if (row[0]; as leftMeter) {
                        <tf-billing-meter
                          [meter]="leftMeter"
                          (showPricing)="openPricingDialog($event)" />
                      }
                      @if (row[1]; as rightMeter) {
                        <tf-billing-meter
                          [meter]="rightMeter"
                          (showPricing)="openPricingDialog($event)" />
                      } @else {
                        <div class="billing-usage-card__meter-placeholder" aria-hidden="true"></div>
                      }
                    </div>
                  }

                  @if (aiMeters.length > 0) {
                    <div class="billing-usage-card__group">
                      <div class="billing-usage-card__group-label">
                        {{ 'SETTINGS_BILLING.PLAN.METERS.AI_TOKENS_GROUP' | translate }}
                      </div>
                      <div class="billing-usage-card__group-grid">
                        @for (meter of aiMeters; track meter.id) {
                          <tf-billing-meter
                            [meter]="meter"
                            (showPricing)="openPricingDialog($event)" />
                        }
                      </div>
                    </div>
                  }

                  @if (isPaidSelfService && accountSpendLimit() !== null) {
                    <div class="billing-usage-card__spend-cap" aria-live="polite">
                      <div class="billing-usage-card__spend-cap-head">
                        <div class="billing-usage-card__spend-cap-title">
                          {{ 'SETTINGS_BILLING.PLAN.SPEND_LIMIT.TITLE' | translate }}
                        </div>
                        <p class="billing-usage-card__spend-cap-summary">
                          @if (spendCapPreset() === 'unlimited') {
                            {{ 'SETTINGS_BILLING.PLAN.SPEND_LIMIT.SUMMARY_UNCAPPED' | translate }}
                          } @else if (spendCapEffectiveMinor() === 0) {
                            {{ 'SETTINGS_BILLING.PLAN.SPEND_LIMIT.SUMMARY_BLOCKING' | translate }}
                          } @else {
                            {{
                              'SETTINGS_BILLING.PLAN.SPEND_LIMIT.SUMMARY_CAPPED'
                                | translate
                                  : {
                                      amount: formatAmount(
                                        spendCapEffectiveMinor(),
                                        accountSpendLimit()?.currency
                                      ),
                                    }
                            }}
                          }
                        </p>
                      </div>
                      <div class="billing-usage-card__spend-cap-controls">
                        <label for="tf-spend-cap-preset" class="sr-only">
                          {{ 'SETTINGS_BILLING.PLAN.SPEND_LIMIT.TITLE' | translate }}
                        </label>
                        <select
                          id="tf-spend-cap-preset"
                          class="billing-usage-card__spend-cap-select"
                          (change)="onSpendCapPresetChange($event)">
                          @for (option of spendCapOptions; track option.value) {
                            <option
                              [value]="option.value"
                              [selected]="option.value === spendCapPreset()">
                              @switch (option.labelKey) {
                                @case ('unlimited') {
                                  {{
                                    'SETTINGS_BILLING.PLAN.SPEND_LIMIT.PRESET_NO_CAP' | translate
                                  }}
                                }
                                @case ('block') {
                                  {{ 'SETTINGS_BILLING.PLAN.SPEND_LIMIT.PRESET_BLOCK' | translate }}
                                }
                                @case ('custom') {
                                  {{
                                    'SETTINGS_BILLING.PLAN.SPEND_LIMIT.PRESET_CUSTOM' | translate
                                  }}
                                }
                                @default {
                                  {{
                                    'SETTINGS_BILLING.PLAN.SPEND_LIMIT.PRESET_AMOUNT'
                                      | translate
                                        : {
                                            amount: formatAmount(
                                              (option.amount ?? 0) * 100,
                                              accountSpendLimit()?.currency
                                            ),
                                          }
                                  }}
                                }
                              }
                            </option>
                          }
                        </select>
                        @if (spendCapPreset() === 'custom') {
                          <div class="billing-usage-card__spend-cap-custom">
                            <span class="billing-usage-card__spend-cap-prefix">€</span>
                            <label for="tf-spend-cap-custom" class="sr-only">
                              {{ 'SETTINGS_BILLING.PLAN.SPEND_LIMIT.PRESET_CUSTOM' | translate }}
                            </label>
                            <input
                              id="tf-spend-cap-custom"
                              class="billing-usage-card__spend-cap-input"
                              type="number"
                              inputmode="decimal"
                              min="0"
                              step="1"
                              [value]="spendCapCustomMajor()"
                              (input)="onSpendCapCustomInput($event)" />
                            <span class="billing-usage-card__spend-cap-suffix">
                              {{ 'SETTINGS_BILLING.PLAN.SPEND_LIMIT.CUSTOM_SUFFIX' | translate }}
                            </span>
                          </div>
                        }
                        <p-button
                          size="small"
                          severity="secondary"
                          [outlined]="true"
                          [label]="'SETTINGS_BILLING.PLAN.SPEND_LIMIT.SAVE_BUTTON' | translate"
                          [loading]="isSavingSpendLimit()"
                          [disabled]="!canSaveAccountSpendLimit()"
                          (onClick)="saveAccountSpendLimit()" />
                      </div>
                      @if (spendLimitSaveFailed()) {
                        <tf-settings-inline-message
                          [message]="'SETTINGS_BILLING.PLAN.SPEND_LIMIT.SAVE_FAILED' | translate"
                          messageId="billing-spend-limit-save-error"
                          (dismissed)="dismissSpendLimitSaveError()" />
                      }
                      @if (spendLimitSaveSucceeded()) {
                        <p class="billing-usage-card__success">
                          {{ 'SETTINGS_BILLING.PLAN.SPEND_LIMIT.SAVE_SUCCEEDED' | translate }}
                        </p>
                      }
                    </div>
                  } @else if (isPaidSelfService && spendLimitsLoadFailed()) {
                    <tf-settings-inline-message
                      [message]="'SETTINGS_BILLING.PLAN.SPEND_LIMIT.LOAD_FAILED' | translate"
                      messageId="billing-spend-limit-load-error"
                      (dismissed)="dismissSpendLimitLoadError()" />
                  }

                  <p class="billing-usage-card__disclaimer">
                    <i class="pi pi-info-circle" aria-hidden="true"></i>
                    @if (isEnterprise) {
                      <span>
                        {{ 'SETTINGS_BILLING.PLAN.USAGE_DISCLAIMER_ENTERPRISE' | translate }}
                      </span>
                    } @else if (isFree) {
                      <span>{{ 'SETTINGS_BILLING.PLAN.USAGE_DISCLAIMER_FREE' | translate }}</span>
                    } @else {
                      <span>{{ 'SETTINGS_BILLING.PLAN.USAGE_DISCLAIMER_PAID' | translate }}</span>
                    }
                  </p>
                </article>

                @if (isEnterprise) {
                  <article class="billing-contact-card">
                    <span class="billing-contact-card__icon" aria-hidden="true">
                      <i class="pi pi-envelope"></i>
                    </span>
                    <div class="billing-contact-card__body">
                      <h4 class="billing-contact-card__title">
                        {{ 'SETTINGS_BILLING.PLAN.CONTACT.TITLE' | translate }}
                      </h4>
                      <p class="billing-contact-card__desc">
                        {{ 'SETTINGS_BILLING.PLAN.CONTACT.DESCRIPTION_PREFIX' | translate }}
                        <a class="billing-link" [href]="'mailto:' + enterpriseContactEmail">
                          {{ enterpriseContactEmail }}
                        </a>
                        {{ 'SETTINGS_BILLING.PLAN.CONTACT.DESCRIPTION_SUFFIX' | translate }}
                      </p>
                    </div>
                    <div class="billing-contact-card__cta">
                      <p-button
                        [label]="'SETTINGS_BILLING.PLAN.CONTACT.BUTTON' | translate"
                        (onClick)="openContactSales()" />
                    </div>
                  </article>
                } @else {
                  <section class="billing-compare">
                    <header class="billing-compare__header">
                      <h3>{{ 'SETTINGS_BILLING.COMPARE.TITLE' | translate }}</h3>
                      <p>{{ 'SETTINGS_BILLING.COMPARE.DESCRIPTION' | translate }}</p>
                      <p class="billing-compare__vat-note">
                        {{ 'SETTINGS_BILLING.PRICE_EXCLUDES_VAT' | translate }}
                      </p>
                    </header>

                    @if (planSwitchFailed(); as planName) {
                      <tf-settings-inline-message
                        [message]="
                          planSwitchErrorMessageKey()
                            | translate
                              : {
                                  plan: planName,
                                }
                        "
                        severity="error"
                        (dismissed)="dismissPlanSwitchError()" />
                    }
                    @if (planSwitchStarted(); as planName) {
                      <p class="settings-page__description billing-plan-switch-success">
                        {{
                          'SETTINGS_BILLING.PLAN_CARDS.SWITCH_STARTED'
                            | translate: { plan: planName }
                        }}
                      </p>
                    }
                    @if (pendingChange(); as change) {
                      <section class="billing-pending-change" aria-live="polite">
                        <div class="billing-pending-change__content">
                          <h4>{{ 'SETTINGS_BILLING.PENDING_CHANGE.TITLE' | translate }}</h4>
                          <p>
                            @if (change.type === 'SEAT_CHANGE') {
                              @if (change.status === 'SCHEDULED_PERIOD_END') {
                                {{
                                  'SETTINGS_BILLING.PENDING_CHANGE.SEAT_SCHEDULED_DESCRIPTION'
                                    | translate
                                      : {
                                          seats: pendingChangeSeatQuantity(),
                                          date: formatDate(change.effectiveAt),
                                        }
                                }}
                              } @else {
                                {{
                                  'SETTINGS_BILLING.PENDING_CHANGE.SEAT_DESCRIPTION'
                                    | translate
                                      : {
                                          seats: pendingChangeSeatQuantity(),
                                        }
                                }}
                              }
                            } @else {
                              {{
                                'SETTINGS_BILLING.PENDING_CHANGE.DESCRIPTION'
                                  | translate
                                    : {
                                        plan:
                                          pendingChangePlanName() ||
                                          ('SETTINGS_BILLING.PENDING_CHANGE.UNKNOWN_PLAN'
                                            | translate),
                                      }
                              }}
                            }
                          </p>
                          @if (planCancelFailed()) {
                            <tf-settings-inline-message
                              [message]="planCancelErrorMessageKey() | translate"
                              messageId="billing-pending-change-cancel-error"
                              (dismissed)="dismissPlanCancelError()" />
                          }
                        </div>
                        <div class="billing-pending-change__actions">
                          @if (change.actionUrl) {
                            <p-button
                              size="small"
                              [label]="'SETTINGS_BILLING.PENDING_CHANGE.RESUME' | translate"
                              (onClick)="resumePendingPlanChange()" />
                          }
                          @if (canCancelPendingPlanChange()) {
                            <p-button
                              size="small"
                              severity="secondary"
                              [outlined]="true"
                              [loading]="isCancelingPlanChange()"
                              [label]="'SETTINGS_BILLING.PENDING_CHANGE.CANCEL' | translate"
                              (onClick)="cancelPendingPlanChange()" />
                          }
                        </div>
                      </section>
                    }

                    @if (comparePlans().length > 0) {
                      <div class="billing-compare__scroll">
                        <table class="billing-compare__table">
                          <caption class="sr-only">
                            {{ 'SETTINGS_BILLING.COMPARE.TITLE' | translate }}
                          </caption>
                          <thead>
                            <tr>
                              <th scope="col" class="billing-compare__feature-col">
                                <span class="sr-only">
                                  {{ 'SETTINGS_BILLING.COMPARE.FEATURE_HEADER' | translate }}
                                </span>
                              </th>
                              @for (option of comparePlans(); track option.id) {
                                <th
                                  scope="col"
                                  class="billing-compare__plan-col"
                                  [class.billing-compare__plan-col--current]="
                                    isCurrentPlan(option)
                                  ">
                                  <div class="billing-compare__plan-head">
                                    <div class="billing-compare__plan-name">
                                      @if (isEnterpriseTeaser(option)) {
                                        {{ 'SETTINGS_BILLING.COMPARE.ENTERPRISE.NAME' | translate }}
                                      } @else {
                                        {{ option.name }}
                                      }
                                    </div>
                                    <div class="billing-compare__plan-price">
                                      @if (isEnterpriseTeaser(option)) {
                                        <span class="billing-compare__plan-price-main">
                                          {{
                                            'SETTINGS_BILLING.COMPARE.ENTERPRISE.PRICE' | translate
                                          }}
                                        </span>
                                        <span class="billing-compare__plan-price-suffix">
                                          {{
                                            'SETTINGS_BILLING.COMPARE.ENTERPRISE.PRICE_SUFFIX'
                                              | translate
                                          }}
                                        </span>
                                      } @else {
                                        <span class="billing-compare__plan-price-main">
                                          @if (isTranslationKey(formatPlanPrimaryPrice(option))) {
                                            {{ formatPlanPrimaryPrice(option) | translate }}
                                          } @else {
                                            {{ formatPlanPrimaryPrice(option) }}
                                          }
                                        </span>
                                        @if (planPriceSuffix(option); as suffix) {
                                          <span class="billing-compare__plan-price-suffix">
                                            @if (isTranslationKey(suffix)) {
                                              {{ suffix | translate }}
                                            } @else {
                                              {{ suffix }}
                                            }
                                          </span>
                                        }
                                      }
                                    </div>
                                    <div class="billing-compare__plan-cta">
                                      @if (isCurrentPlan(option)) {
                                        <span class="billing-compare__current-tag">
                                          <i class="pi pi-check" aria-hidden="true"></i>
                                          {{
                                            'SETTINGS_BILLING.PLAN_CARDS.CURRENT_BADGE' | translate
                                          }}
                                        </span>
                                      } @else if (isContactSalesPlan(option)) {
                                        <p-button
                                          size="small"
                                          styleClass="billing-compare__switch-btn"
                                          [label]="
                                            'SETTINGS_BILLING.COMPARE.ENTERPRISE.CTA' | translate
                                          "
                                          (onClick)="openContactSales()" />
                                      } @else {
                                        <p-button
                                          size="small"
                                          styleClass="billing-compare__switch-btn"
                                          [label]="
                                            'SETTINGS_BILLING.PLAN_CARDS.SWITCH_BUTTON' | translate
                                          "
                                          [disabled]="!isSwitchablePlan(option)"
                                          [loading]="switchingPlanId() === option.id"
                                          (onClick)="openPlanChangeDialog(option)" />
                                      }
                                    </div>
                                  </div>
                                </th>
                              }
                            </tr>
                          </thead>
                          <tbody>
                            @for (row of compareRows(); track row.id) {
                              <tr>
                                <th scope="row" class="billing-compare__row-label">
                                  {{ row.labelKey | translate }}
                                </th>
                                @for (cell of row.cells; track $index) {
                                  <td
                                    class="billing-compare__cell"
                                    [class.billing-compare__cell--current]="
                                      isCurrentPlan(comparePlans()[$index]!)
                                    ">
                                    @switch (cell.kind) {
                                      @case ('check') {
                                        <i
                                          class="pi pi-check billing-compare__check"
                                          aria-hidden="true"></i>
                                        <span class="sr-only">
                                          {{ 'SETTINGS_BILLING.COMPARE.INCLUDED' | translate }}
                                        </span>
                                      }
                                      @case ('text') {
                                        @if (cell.isTranslationKey) {
                                          {{ cell.value | translate }}
                                        } @else {
                                          {{ cell.value }}
                                        }
                                      }
                                    }
                                  </td>
                                }
                              </tr>
                            }
                          </tbody>
                        </table>
                      </div>
                    } @else {
                      <p class="settings-page__description">
                        {{ 'SETTINGS_BILLING.PLAN.NO_AVAILABLE_PLANS' | translate }}
                      </p>
                    }
                  </section>
                }
              </section>
            } @else {
              <section class="billing-empty-state">
                <h3 class="settings-page__section-title">
                  {{ 'SETTINGS_BILLING.PLAN.NO_ACTIVE_TITLE' | translate }}
                </h3>
                <p class="settings-page__description">
                  {{ 'SETTINGS_BILLING.PLAN.NO_ACTIVE_DESCRIPTION' | translate }}
                </p>
              </section>
            }
          </div>
        </p-tabpanel>

        <p-tabpanel value="profile">
          <div class="billing-tab-content">
            <tf-billing-profile-tab
              [active]="activeTab() === 'profile'"
              [showPaymentMethod]="canShowPaymentMethodCard()"
              [enterpriseNotice]="isEnterprisePlan()"
              [enterpriseContactEmail]="enterpriseContactEmail"
              (contactSalesRequested)="openContactSales()" />
          </div>
        </p-tabpanel>

        <p-tabpanel value="invoices">
          <div class="billing-tab-content">
            @if (showInitialOverviewError()) {
              <tf-settings-load-error
                [title]="'SETTINGS_BILLING.ERROR.LOAD_FAILED_TITLE' | translate"
                [message]="'SETTINGS_BILLING.ERROR.LOAD_FAILED_DESCRIPTION' | translate"
                [details]="overviewLoadErrorDetails()"
                [dense]="true"
                [retrying]="isLoadingOverview()"
                (retry)="refresh()" />
            } @else if (showInitialOverviewLoading()) {
              <section class="billing-section billing-invoices-skeleton" aria-hidden="true">
                <div class="billing-skeleton-card billing-skeleton-card--invoices-access">
                  <span class="billing-skeleton-bar billing-skeleton-bar--title"></span>
                  <span class="billing-skeleton-bar billing-skeleton-bar--description"></span>
                </div>
                <tf-settings-loading-placeholder [rows]="5" [columns]="invoiceColumns.length" />
              </section>
            } @else {
              <tf-settings-inline-message
                [message]="
                  showOverviewReloadError()
                    ? ('SETTINGS_BILLING.ERROR.LOAD_FAILED_DESCRIPTION' | translate)
                    : null
                "
                messageId="billing-invoices-overview-reload-error"
                (dismissed)="dismissOverviewReloadError()" />

              @if (invoiceAccessMode() === invoiceAccessModes.EnterpriseManaged) {
                <section class="billing-section">
                  <article class="billing-contact-card">
                    <span class="billing-contact-card__icon" aria-hidden="true">
                      <i class="pi pi-envelope"></i>
                    </span>
                    <div class="billing-contact-card__body">
                      <h4 class="billing-contact-card__title">
                        {{ 'SETTINGS_BILLING.INVOICES.ENTERPRISE_TITLE' | translate }}
                      </h4>
                      <p class="billing-contact-card__desc">
                        {{ 'SETTINGS_BILLING.PLAN.CONTACT.DESCRIPTION_PREFIX' | translate }}
                        <a class="billing-link" [href]="'mailto:' + enterpriseContactEmail">
                          {{ enterpriseContactEmail }}
                        </a>
                        {{ 'SETTINGS_BILLING.PLAN.CONTACT.DESCRIPTION_SUFFIX' | translate }}
                      </p>
                    </div>
                    <div class="billing-contact-card__cta">
                      <p-button
                        [label]="'SETTINGS_BILLING.PLAN.CONTACT.BUTTON' | translate"
                        (onClick)="openContactSales()" />
                    </div>
                  </article>
                </section>
              } @else if (invoiceAccessMode() === invoiceAccessModes.NoInvoicesFree) {
                <section class="billing-empty-state">
                  <h3 class="settings-page__section-title">
                    {{ 'SETTINGS_BILLING.INVOICES.NO_INVOICES_TITLE' | translate }}
                  </h3>
                  <p class="settings-page__description">
                    {{ 'SETTINGS_BILLING.INVOICES.FREE_DESCRIPTION' | translate }}
                  </p>
                </section>
              } @else if (canDownloadInvoices()) {
                <section class="billing-section billing-section--table">
                  @if (!showInitialInvoicesLoading() && !showInitialInvoicesError()) {
                    <div class="billing-tab-toolbar">
                      <p-button
                        size="small"
                        severity="secondary"
                        [outlined]="true"
                        icon="pi pi-refresh"
                        [label]="'GENERAL.REFRESH' | translate"
                        [loading]="isLoadingInvoices()"
                        [disabled]="isLoadingInvoices()"
                        (onClick)="reloadInvoicesTab()" />
                    </div>
                  }

                  <tf-settings-inline-message
                    [message]="
                      invoiceDownloadFailed()
                        ? ('SETTINGS_BILLING.INVOICES.DOWNLOAD_FAILED' | translate)
                        : null
                    "
                    messageId="billing-invoice-download-error"
                    (dismissed)="dismissInvoiceDownloadError()" />

                  <tf-settings-inline-message
                    [message]="
                      showInvoiceReloadError()
                        ? ('SETTINGS_BILLING.INVOICES.LOAD_FAILED' | translate)
                        : null
                    "
                    messageId="billing-invoices-reload-error"
                    (dismissed)="dismissInvoiceLoadError()" />

                  @if (showInitialInvoicesLoading()) {
                    <tf-settings-loading-placeholder [rows]="5" [columns]="invoiceColumns.length" />
                  } @else if (showInitialInvoicesError()) {
                    <tf-settings-load-error
                      [title]="'SETTINGS_BILLING.INVOICES.LOAD_FAILED' | translate"
                      [message]="'SETTINGS_BILLING.ERROR.LOAD_FAILED_DESCRIPTION' | translate"
                      [details]="invoiceLoadErrorDetails()"
                      [dense]="true"
                      [retrying]="isLoadingInvoices()"
                      (retry)="retryInvoices()" />
                  } @else {
                    <tf-data-table
                      [columns]="invoiceColumns"
                      [value]="invoiceTableRows()"
                      dataKey="id"
                      styleClass="p-datatable-sm billing-invoices-table"
                      emptyMessageKey="SETTINGS_BILLING.INVOICES.EMPTY">
                      <ng-template tfColumnDef="number" let-invoice>
                        {{ invoice.number || invoice.id }}
                      </ng-template>
                      <ng-template tfColumnDef="_actions" let-invoice>
                        <div class="billing-invoices-table__actions">
                          <button
                            type="button"
                            class="billing-invoice-download"
                            [disabled]="downloadingInvoiceId() === invoice.id"
                            (click)="downloadInvoice(invoice.id)">
                            @if (downloadingInvoiceId() === invoice.id) {
                              {{ 'GENERAL.LOADING' | translate }}
                            } @else {
                              {{ 'SETTINGS_BILLING.INVOICES.DOWNLOAD' | translate }}
                            }
                          </button>
                        </div>
                      </ng-template>
                    </tf-data-table>
                  }
                </section>
              } @else {
                <section class="billing-empty-state">
                  <h3 class="settings-page__section-title">
                    {{ 'SETTINGS_BILLING.INVOICES.NO_INVOICES_TITLE' | translate }}
                  </h3>
                  <p class="settings-page__description">
                    {{ 'SETTINGS_BILLING.INVOICES.NO_ACTIVE_DESCRIPTION' | translate }}
                  </p>
                </section>
              }
            }
          </div>
        </p-tabpanel>

        @if (canShowCancellationTab()) {
          <p-tabpanel value="cancellation">
            <div class="billing-tab-content">
              @if (showInitialOverviewError()) {
                <tf-settings-load-error
                  [title]="'SETTINGS_BILLING.ERROR.LOAD_FAILED_TITLE' | translate"
                  [message]="'SETTINGS_BILLING.ERROR.LOAD_FAILED_DESCRIPTION' | translate"
                  [details]="overviewLoadErrorDetails()"
                  [dense]="true"
                  [retrying]="isLoadingOverview()"
                  (retry)="refresh()" />
              } @else if (showInitialOverviewLoading()) {
                <section class="billing-section billing-cancellation-skeleton" aria-hidden="true">
                  <span class="billing-skeleton-bar billing-skeleton-bar--title"></span>
                  <span class="billing-skeleton-bar billing-skeleton-bar--description"></span>
                  <div class="billing-skeleton-card billing-skeleton-card--cancellation">
                    <span class="billing-skeleton-icon"></span>
                    <div>
                      <span class="billing-skeleton-bar billing-skeleton-bar--medium"></span>
                      <span class="billing-skeleton-bar billing-skeleton-bar--description"></span>
                    </div>
                    <span class="billing-skeleton-button"></span>
                  </div>
                </section>
              } @else if (currentPlan(); as plan) {
                <section class="billing-section">
                  <tf-settings-inline-message
                    [message]="
                      showOverviewReloadError()
                        ? ('SETTINGS_BILLING.ERROR.LOAD_FAILED_DESCRIPTION' | translate)
                        : null
                    "
                    messageId="billing-cancellation-overview-reload-error"
                    (dismissed)="dismissOverviewReloadError()" />

                  <header class="billing-section-header">
                    <div class="billing-section-header__intro">
                      <h3>{{ 'SETTINGS_BILLING.CANCELLATION.TITLE' | translate }}</h3>
                      <p>
                        @if (plan.planKind === planKinds.Free) {
                          {{ 'SETTINGS_BILLING.CANCELLATION.FREE_DESCRIPTION' | translate }}
                        } @else {
                          {{ 'SETTINGS_BILLING.CANCELLATION.SELF_SERVE_DESCRIPTION' | translate }}
                        }
                      </p>
                    </div>
                  </header>

                  <article class="billing-cancellation-card">
                    @if (isBillingCanceling()) {
                      <span class="billing-cancellation-card__icon" aria-hidden="true">
                        <i class="pi pi-clock"></i>
                      </span>
                      <div class="billing-cancellation-card__body">
                        <h4>{{ 'SETTINGS_BILLING.CANCELLATION.SCHEDULED_TITLE' | translate }}</h4>
                        <p>
                          @if (cancellationEffectiveAt(); as effectiveAt) {
                            {{
                              'SETTINGS_BILLING.CANCELLATION.SCHEDULED_DESCRIPTION_WITH_DATE'
                                | translate
                                  : {
                                      date: (effectiveAt | date: 'mediumDate'),
                                    }
                            }}
                          } @else {
                            {{ 'SETTINGS_BILLING.CANCELLATION.SCHEDULED_DESCRIPTION' | translate }}
                          }
                        </p>
                      </div>
                    } @else {
                      <span class="billing-cancellation-card__icon" aria-hidden="true">
                        <i class="pi pi-exclamation-triangle"></i>
                      </span>
                      <div class="billing-cancellation-card__body">
                        <h4>{{ 'SETTINGS_BILLING.CANCELLATION.CARD_TITLE' | translate }}</h4>
                        <p>
                          @if (plan.planKind === planKinds.Free) {
                            {{ 'SETTINGS_BILLING.CANCELLATION.FREE_WARNING' | translate }}
                          } @else {
                            {{ 'SETTINGS_BILLING.CANCELLATION.SELF_SERVE_WARNING' | translate }}
                          }
                        </p>
                        @if (hasPendingPlanChange()) {
                          <tf-settings-inline-message
                            [message]="
                              'SETTINGS_BILLING.CANCELLATION.PENDING_CHANGE_BLOCKED' | translate
                            "
                            [closable]="false"
                            messageId="billing-cancellation-pending-change-error" />
                        }
                        @if (subscriptionCancelFailed()) {
                          <tf-settings-inline-message
                            [message]="'SETTINGS_BILLING.CANCELLATION.FAILED' | translate"
                            messageId="billing-subscription-cancel-error"
                            (dismissed)="dismissSubscriptionCancelError()" />
                        }
                        @if (subscriptionCancelStarted()) {
                          <p class="billing-cancellation-card__success">
                            {{ 'SETTINGS_BILLING.CANCELLATION.STARTED' | translate }}
                          </p>
                        }
                      </div>
                      <div class="billing-cancellation-card__cta">
                        <p-button
                          severity="danger"
                          [loading]="isCancelingSubscription()"
                          [disabled]="!canCancelSubscription()"
                          [label]="'SETTINGS_BILLING.CANCELLATION.BUTTON' | translate"
                          (onClick)="cancelSubscription()" />
                      </div>
                    }
                  </article>
                </section>
              }
            </div>
          </p-tabpanel>
        }
      </p-tabpanels>
    </p-tabs>
  </div>

  <tf-update-seats-dialog
    [open]="isSeatDialogOpen()"
    [currentSeats]="seatsCurrent()"
    [activeUsers]="seatsActiveUsers()"
    [pricePerSeatMinor]="seatsPricePerSeatMinor()"
    [currency]="seatsCurrency()"
    [isConfirming]="isChangingSeats()"
    (closed)="closeSeatDialog()"
    (confirmed)="onSeatsConfirmed($event)" />

  <tf-plan-change-dialog
    [open]="planChangeDialogTarget() !== null"
    [currentPlan]="currentPlan()"
    [targetPlan]="planChangeDialogTarget()"
    [currentPrice]="planChangeDialogCurrentPrice()"
    [currentPriceSuffix]="planChangeDialogCurrentPriceSuffix()"
    [targetPrice]="planChangeDialogTargetPrice()"
    [targetPriceSuffix]="planChangeDialogTargetPriceSuffix()"
    [requiresBillingTerms]="planChangeDialogRequiresBillingTerms()"
    [isConfirming]="planChangeDialogTarget()?.id === switchingPlanId()"
    (closed)="closePlanChangeDialog()"
    (confirmed)="onPlanChangeConfirmed($event)" />

  <tf-billing-pricing-tiers-dialog [tiers]="pricingDialog()" (closed)="closePricingDialog()" />
</div>
